import { Case, CaseStatus } from '@/api/apis/case';
import { getLateralityBlockReason, getProceedActionHintMessage, type ProceedBlockReason } from '@/utils/helpers';
import { useTrialRandomizationStatus } from './useTrialRandomizationStatus';

type UseProceedBlockParams = {
  caseData: Case;
  nextStatus?: CaseStatus;
  targetProceedStatus?: CaseStatus;
  canMoveToNextStatus: boolean;
  isProceeding: boolean;
};

export const useProceedBlock = ({
  caseData,
  nextStatus,
  targetProceedStatus,
  canMoveToNextStatus,
  isProceeding,
}: UseProceedBlockParams) => {
  const {
    organType,
    donorType,
    laterality,
    serviceType,
    txCenterDetails: txCenter,
    trialProgram: caseTrialProgram,
    questionnaire,
    randomization,
    isRandomizationRequired,
    organApproval,
    reAllocationCaseId,
    opoRequest,
  } = caseData;

  const lateralityBlockReason = getLateralityBlockReason({
    organType,
    laterality,
    nextStatus: targetProceedStatus ?? nextStatus,
  });

  const isLateralityBlock = lateralityBlockReason !== null;

  const { proceedOnOCSDisabled, proceedPendingLogisticsDisabled, proceedBlockReason } = useTrialRandomizationStatus({
    organType,
    donorType,
    nextStatus,
    serviceType,
    canMoveToNextStatus,
    txCenter,
    caseTrialProgram,
    questionnaire,
    randomization,
    isRandomizationRequired,
    organApproval,
    isReAllocationCase: !!reAllocationCaseId,
    opoRequest,
  });

  const proceedDisabled = proceedPendingLogisticsDisabled || proceedOnOCSDisabled || isLateralityBlock;
  const proceedInteractionDisabled = proceedDisabled || isProceeding;

  const proceedBlockReasons: ProceedBlockReason[] = [lateralityBlockReason, proceedBlockReason];
  const activeBlockReason = proceedBlockReasons.find((reason) => reason !== null) ?? null;
  const actionHintMessage = getProceedActionHintMessage(activeBlockReason);

  return { proceedDisabled, proceedInteractionDisabled, actionHintMessage };
};
